import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import CustomSelect from '../components/CustomSelect';

// Types for medical records
interface MedicalRecord {
  id: number;
  recordType: string;
  title: string;
  description: string;
  recordDate: string;
  category: string;
  status: string;
  followUpRequired: boolean;
  followUpDate?: string;
  providerName: string;
  createdAt: string;
}

interface Medication {
  id: number;
  medicationName: string;
  dosage: string;
  frequency: string;
  route: string;
  startDate: string;
  endDate?: string;
  status: string;
  indication?: string;
  instructions?: string;
  prescribedByName: string;
  prescribedDate: string;
  refillsRemaining: number;
}

interface VitalSign {
  id: number;
  recordedDate: string;
  bloodPressureSystolic?: number;
  bloodPressureDiastolic?: number;
  heartRate?: number;
  temperature?: number;
  respiratoryRate?: number;
  oxygenSaturation?: number;
  weight?: number;
  height?: number;
  painLevel?: number;
  bloodGlucose?: number;
  notes?: string;
  recordedByName: string;
}

interface Allergy {
  id: number;
  allergen: string;
  allergyType: string;
  severity: string;
  reaction: string;
  status: string;
  discoveredDate: string;
  treatment?: string;
  notes?: string;
  reportedByName: string;
}

interface MedicalCondition {
  id: number;
  conditionName: string;
  icd10Code?: string;
  diagnosisDate?: string;
  status: string;
  severity: string;
  ongoingTreatment: boolean;
  treatmentPlan?: string;
  notes?: string;
  diagnosedByName: string;
  lastReviewDate?: string;
  nextReviewDate?: string;
}

interface Resident {
  id: number;
  name: string;
  age: number;
  diagnosis: string;
  photo?: string;
}

const MedicalRecords: React.FC = () => {
  const { isAdmin } = useAuth();
  const [residents, setResidents] = useState<Resident[]>([]);
  const [selectedResident, setSelectedResident] = useState<Resident | null>(null);
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Medical data states
  const [medicalRecords, setMedicalRecords] = useState<MedicalRecord[]>([]);
  const [medications, setMedications] = useState<Medication[]>([]);
  const [vitalSigns, setVitalSigns] = useState<VitalSign[]>([]);
  const [allergies, setAllergies] = useState<Allergy[]>([]);
  const [medicalConditions, setMedicalConditions] = useState<MedicalCondition[]>([]);

  // Modal states
  const [showAddMedication, setShowAddMedication] = useState(false);
  const [showAddVitals, setShowAddVitals] = useState(false);
  const [showAddAllergy, setShowAddAllergy] = useState(false);
  const [showAddCondition, setShowAddCondition] = useState(false);

  // Fetch residents
  useEffect(() => {
    const fetchResidents = async () => {
      try {
        const response = await api.get('/residents');
        if (response.data.success) {
          setResidents(response.data.data.residents);
          if (response.data.data.residents.length > 0) {
            setSelectedResident(response.data.data.residents[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching residents:', error);
        setError('Failed to fetch residents');
      }
    };

    fetchResidents();
  }, []);

  // Fetch medical data when resident is selected
  useEffect(() => {
    if (selectedResident) {
      fetchMedicalData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResident]);

  const fetchMedicalData = async () => {
    if (!selectedResident) return;
    
    setLoading(true);
    try {
      const [recordsRes, medicationsRes, vitalsRes, allergiesRes, conditionsRes] = await Promise.all([
        api.get(`/medical/residents/${selectedResident.id}/records`),
        api.get(`/medical/residents/${selectedResident.id}/medications`),
        api.get(`/medical/residents/${selectedResident.id}/vitals`),
        api.get(`/medical/residents/${selectedResident.id}/allergies`),
        api.get(`/medical/residents/${selectedResident.id}/conditions`)
      ]);

      if (recordsRes.data.success) {
        setMedicalRecords(recordsRes.data.data.records);
      }
      if (medicationsRes.data.success) {
        setMedications(medicationsRes.data.data);
      }
      if (vitalsRes.data.success) {
        setVitalSigns(vitalsRes.data.data);
      }
      if (allergiesRes.data.success) {
        setAllergies(allergiesRes.data.data);
      }
      if (conditionsRes.data.success) {
        setMedicalConditions(conditionsRes.data.data);
      }
    } catch (error) {
      console.error('Error fetching medical data:', error);
      setError('Failed to fetch medical data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'severe':
        return 'bg-red-100 text-red-800';
      case 'moderate':
        return 'bg-orange-100 text-orange-800';
      case 'mild':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const renderOverview = () => (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Records</p>
              <p className="text-2xl font-bold text-gray-900">{medicalRecords.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Medications</p>
              <p className="text-2xl font-bold text-gray-900">
                {medications.filter(med => med.status === 'active').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Allergies</p>
              <p className="text-2xl font-bold text-gray-900">
                {allergies.filter(allergy => allergy.status === 'active').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-primary-100 rounded-lg">
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Conditions</p>
              <p className="text-2xl font-bold text-gray-900">
                {medicalConditions.filter(condition => condition.status === 'active').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Records */}
      <div className="bg-white card-list">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Medical Records</h3>
        </div>
        <div className="p-6">
          {medicalRecords.length === 0 ? (
            <p className="text-gray-500">No medical records found.</p>
          ) : (
            <div className="space-y-4">
              {medicalRecords.slice(0, 5).map((record) => (
                <div key={record.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-medium text-gray-900">{record.title}</span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 badge-pill ${
                          record.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {record.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{record.description}</p>
                      <div className="flex items-center text-xs text-gray-500 space-x-4">
                        <span>Type: {record.recordType}</span>
                        <span>Provider: {record.providerName}</span>
                        <span>Date: {formatDate(record.recordDate)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderMedications = () => (
    <div className="bg-white card-list">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Medications</h3>
        <button
          onClick={() => setShowAddMedication(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700"
        >
          Add Medication
        </button>
      </div>
      <div className="p-6">
        {medications.length === 0 ? (
          <p className="text-gray-500">No medications found.</p>
        ) : (
          <div className="space-y-4">
            {medications.map((medication) => (
              <div key={medication.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="text-lg font-medium text-gray-900">{medication.medicationName}</h4>
                      <span className={`inline-flex items-center px-2.5 py-0.5 badge-pill ${
                        medication.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {medication.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-600">Dosage:</span> {medication.dosage}
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Frequency:</span> {medication.frequency}
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Route:</span> {medication.route}
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Start Date:</span> {formatDate(medication.startDate)}
                      </div>
                      {medication.endDate && (
                        <div>
                          <span className="font-medium text-gray-600">End Date:</span> {formatDate(medication.endDate)}
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-gray-600">Refills:</span> {medication.refillsRemaining}
                      </div>
                    </div>
                    {medication.indication && (
                      <div className="mt-2">
                        <span className="font-medium text-gray-600">Indication:</span> {medication.indication}
                      </div>
                    )}
                    {medication.instructions && (
                      <div className="mt-2">
                        <span className="font-medium text-gray-600">Instructions:</span> {medication.instructions}
                      </div>
                    )}
                    <div className="mt-2 text-xs text-gray-500">
                      Prescribed by {medication.prescribedByName} on {formatDate(medication.prescribedDate)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderVitalSigns = () => (
    <div className="bg-white card-list">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Vital Signs</h3>
        <button
          onClick={() => setShowAddVitals(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700"
        >
          Record Vitals
        </button>
      </div>
      <div className="p-6">
        {vitalSigns.length === 0 ? (
          <p className="text-gray-500">No vital signs recorded.</p>
        ) : (
          <div className="space-y-4">
            {vitalSigns.map((vital) => (
              <div key={vital.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="text-sm font-medium text-gray-900">
                    {formatDateTime(vital.recordedDate)}
                  </div>
                  <div className="text-xs text-gray-500">
                    Recorded by {vital.recordedByName}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {vital.bloodPressureSystolic && vital.bloodPressureDiastolic && (
                    <div>
                      <span className="font-medium text-gray-600">Blood Pressure:</span><br />
                      {vital.bloodPressureSystolic}/{vital.bloodPressureDiastolic} mmHg
                    </div>
                  )}
                  {vital.heartRate && (
                    <div>
                      <span className="font-medium text-gray-600">Heart Rate:</span><br />
                      {vital.heartRate} bpm
                    </div>
                  )}
                  {vital.temperature && (
                    <div>
                      <span className="font-medium text-gray-600">Temperature:</span><br />
                      {vital.temperature}°F
                    </div>
                  )}
                  {vital.oxygenSaturation && (
                    <div>
                      <span className="font-medium text-gray-600">O2 Saturation:</span><br />
                      {vital.oxygenSaturation}%
                    </div>
                  )}
                  {vital.weight && (
                    <div>
                      <span className="font-medium text-gray-600">Weight:</span><br />
                      {vital.weight} lbs
                    </div>
                  )}
                  {vital.painLevel && (
                    <div>
                      <span className="font-medium text-gray-600">Pain Level:</span><br />
                      {vital.painLevel}/10
                    </div>
                  )}
                  {vital.bloodGlucose && (
                    <div>
                      <span className="font-medium text-gray-600">Blood Glucose:</span><br />
                      {vital.bloodGlucose} mg/dL
                    </div>
                  )}
                </div>
                {vital.notes && (
                  <div className="mt-3 text-sm">
                    <span className="font-medium text-gray-600">Notes:</span> {vital.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderAllergies = () => (
    <div className="bg-white card-list">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Allergies</h3>
        <button
          onClick={() => setShowAddAllergy(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700"
        >
          Add Allergy
        </button>
      </div>
      <div className="p-6">
        {allergies.length === 0 ? (
          <p className="text-gray-500">No allergies recorded.</p>
        ) : (
          <div className="space-y-4">
            {allergies.map((allergy) => (
              <div key={allergy.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="text-lg font-medium text-gray-900">{allergy.allergen}</h4>
                      <span className={`inline-flex items-center px-2.5 py-0.5 badge-pill ${getSeverityColor(allergy.severity)}`}>
                        {allergy.severity}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 badge-pill ${
                        allergy.status === 'active' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {allergy.status}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium text-gray-600">Type:</span> {allergy.allergyType}
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Reaction:</span> {allergy.reaction}
                      </div>
                      <div>
                        <span className="font-medium text-gray-600">Discovered:</span> {formatDate(allergy.discoveredDate)}
                      </div>
                      {allergy.treatment && (
                        <div>
                          <span className="font-medium text-gray-600">Treatment:</span> {allergy.treatment}
                        </div>
                      )}
                      {allergy.notes && (
                        <div>
                          <span className="font-medium text-gray-600">Notes:</span> {allergy.notes}
                        </div>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Reported by {allergy.reportedByName}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderConditions = () => (
    <div className="bg-white card-list">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Medical Conditions</h3>
        <button
          onClick={() => setShowAddCondition(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700"
        >
          Add Condition
        </button>
      </div>
      <div className="p-6">
        {medicalConditions.length === 0 ? (
          <p className="text-gray-500">No medical conditions recorded.</p>
        ) : (
          <div className="space-y-4">
            {medicalConditions.map((condition) => (
              <div key={condition.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="text-lg font-medium text-gray-900">{condition.conditionName}</h4>
                      <span className={`inline-flex items-center px-2.5 py-0.5 badge-pill ${getSeverityColor(condition.severity)}`}>
                        {condition.severity}
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 badge-pill ${
                        condition.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {condition.status}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      {condition.icd10Code && (
                        <div>
                          <span className="font-medium text-gray-600">ICD-10 Code:</span> {condition.icd10Code}
                        </div>
                      )}
                      {condition.diagnosisDate && (
                        <div>
                          <span className="font-medium text-gray-600">Diagnosis Date:</span> {formatDate(condition.diagnosisDate)}
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-gray-600">Ongoing Treatment:</span> {condition.ongoingTreatment ? 'Yes' : 'No'}
                      </div>
                      {condition.treatmentPlan && (
                        <div>
                          <span className="font-medium text-gray-600">Treatment Plan:</span> {condition.treatmentPlan}
                        </div>
                      )}
                      {condition.notes && (
                        <div>
                          <span className="font-medium text-gray-600">Notes:</span> {condition.notes}
                        </div>
                      )}
                      {condition.nextReviewDate && (
                        <div>
                          <span className="font-medium text-gray-600">Next Review:</span> {formatDate(condition.nextReviewDate)}
                        </div>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Diagnosed by {condition.diagnosedByName}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600">You need administrator privileges to access medical records.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="md:flex md:items-center md:justify-between mb-6">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Medical Records
              </h2>
            </div>
          </div>

          {/* Resident Selection */}
          <div className="bg-white card-list mb-6">
            <div className="px-6 py-4">
              <label htmlFor="resident-select" className="block text-sm font-medium text-gray-700 mb-2">
                Select Resident
              </label>
              <div className="w-full max-w-xs">
                <CustomSelect
                  id="resident-select"
                  value={selectedResident?.id ? String(selectedResident.id) : ''}
                  onChange={(v) => {
                    const resident = residents.find(r => r.id === parseInt(v));
                    setSelectedResident(resident || null);
                  }}
                  placeholder="Select a resident..."
                  options={[
                    { value: '', label: 'Select a resident...' },
                    ...residents.map((resident) => ({
                      value: String(resident.id),
                      label: `${resident.name} (Age: ${resident.age})`,
                    })),
                  ]}
                />
              </div>
            </div>
          </div>

          {selectedResident && (
            <>
              {/* Selected Resident Info */}
              <div className="bg-white card-list mb-6">
                <div className="px-6 py-4">
                  <div className="flex items-center space-x-4">
                    {selectedResident.photo ? (
                      <img 
                        src={selectedResident.photo} 
                        alt={selectedResident.name}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                        <span className="text-blue-600 font-semibold text-xl">
                          {selectedResident.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{selectedResident.name}</h3>
                      <p className="text-sm text-gray-600">Age: {selectedResident.age}</p>
                      <p className="text-sm text-gray-600">Diagnosis: {selectedResident.diagnosis}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="bg-white card-list">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                    {[
                      { key: 'overview', name: 'Overview', icon: '📊' },
                      { key: 'medications', name: 'Medications', icon: '💊' },
                      { key: 'vitals', name: 'Vital Signs', icon: '❤️' },
                      { key: 'allergies', name: 'Allergies', icon: '⚠️' },
                      { key: 'conditions', name: 'Conditions', icon: '🏥' }
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                          activeTab === tab.key
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <span className="mr-2">{tab.icon}</span>
                        {tab.name}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    </div>
                  ) : (
                    <>
                      {activeTab === 'overview' && renderOverview()}
                      {activeTab === 'medications' && renderMedications()}
                      {activeTab === 'vitals' && renderVitalSigns()}
                      {activeTab === 'allergies' && renderAllergies()}
                      {activeTab === 'conditions' && renderConditions()}
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          {error && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Medication Modal */}
      {showAddMedication && (
        <AddMedicationModal
          residentId={selectedResident!.id}
          onClose={() => setShowAddMedication(false)}
          onSuccess={() => {
            setShowAddMedication(false);
            fetchMedicalData();
          }}
        />
      )}

      {/* Add Vital Signs Modal */}
      {showAddVitals && (
        <AddVitalSignsModal
          residentId={selectedResident!.id}
          onClose={() => setShowAddVitals(false)}
          onSuccess={() => {
            setShowAddVitals(false);
            fetchMedicalData();
          }}
        />
      )}

      {/* Add Allergy Modal */}
      {showAddAllergy && (
        <AddAllergyModal
          residentId={selectedResident!.id}
          onClose={() => setShowAddAllergy(false)}
          onSuccess={() => {
            setShowAddAllergy(false);
            fetchMedicalData();
          }}
        />
      )}

      {/* Add Condition Modal */}
      {showAddCondition && (
        <AddConditionModal
          residentId={selectedResident!.id}
          onClose={() => setShowAddCondition(false)}
          onSuccess={() => {
            setShowAddCondition(false);
            fetchMedicalData();
          }}
        />
      )}
    </div>
  );
};

interface AddMedicationModalProps {
  residentId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const AddMedicationModal: React.FC<AddMedicationModalProps> = ({ residentId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    medicationName: '',
    dosage: '',
    frequency: '',
    route: 'oral',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    indication: '',
    instructions: '',
    pharmacy: '',
    refillsRemaining: 0
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await api.post(`/medical/residents/${residentId}/medications`, {
        ...formData,
        endDate: formData.endDate || null
      });

      if (response.data.success) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add medication');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Add Medication</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medication Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.medicationName}
                onChange={(e) => setFormData({ ...formData, medicationName: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., Aspirin, Lisinopril"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dosage <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.dosage}
                onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., 10mg, 5ml"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frequency <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.frequency}
                onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., Once daily, Twice daily"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Route
              </label>
              <CustomSelect
                value={formData.route}
                onChange={(v) => setFormData({ ...formData, route: v })}
                options={[
                  { value: 'oral', label: 'Oral' },
                  { value: 'injection', label: 'Injection' },
                  { value: 'topical', label: 'Topical' },
                  { value: 'inhalation', label: 'Inhalation' },
                  { value: 'other', label: 'Other' },
                ]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date (Optional)
              </label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pharmacy
              </label>
              <input
                type="text"
                value={formData.pharmacy}
                onChange={(e) => setFormData({ ...formData, pharmacy: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Pharmacy name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Refills Remaining
              </label>
              <input
                type="number"
                min="0"
                value={formData.refillsRemaining}
                onChange={(e) => setFormData({ ...formData, refillsRemaining: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Indication
              </label>
              <input
                type="text"
                value={formData.indication}
                onChange={(e) => setFormData({ ...formData, indication: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="What is this medication for?"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instructions
              </label>
              <textarea
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Special instructions for taking this medication"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400"
            >
              {submitting ? 'Adding...' : 'Add Medication'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface AddVitalSignsModalProps {
  residentId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const AddVitalSignsModal: React.FC<AddVitalSignsModalProps> = ({ residentId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    recordedDate: new Date().toISOString().split('T')[0],
    bloodPressureSystolic: '',
    bloodPressureDiastolic: '',
    heartRate: '',
    temperature: '',
    respiratoryRate: '',
    oxygenSaturation: '',
    weight: '',
    height: '',
    painLevel: '',
    bloodGlucose: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        recordedDate: formData.recordedDate,
        bloodPressureSystolic: formData.bloodPressureSystolic ? parseInt(formData.bloodPressureSystolic) : null,
        bloodPressureDiastolic: formData.bloodPressureDiastolic ? parseInt(formData.bloodPressureDiastolic) : null,
        heartRate: formData.heartRate ? parseInt(formData.heartRate) : null,
        temperature: formData.temperature ? parseFloat(formData.temperature) : null,
        respiratoryRate: formData.respiratoryRate ? parseInt(formData.respiratoryRate) : null,
        oxygenSaturation: formData.oxygenSaturation ? parseInt(formData.oxygenSaturation) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        painLevel: formData.painLevel ? parseInt(formData.painLevel) : null,
        bloodGlucose: formData.bloodGlucose ? parseInt(formData.bloodGlucose) : null,
        notes: formData.notes || null
      };

      const response = await api.post(`/medical/residents/${residentId}/vitals`, payload);

      if (response.data.success) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to record vital signs');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Record Vital Signs</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date & Time <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={formData.recordedDate}
                onChange={(e) => setFormData({ ...formData, recordedDate: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Blood Pressure - Systolic (mmHg)
              </label>
              <input
                type="number"
                min="0"
                max="300"
                value={formData.bloodPressureSystolic}
                onChange={(e) => setFormData({ ...formData, bloodPressureSystolic: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., 120"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Blood Pressure - Diastolic (mmHg)
              </label>
              <input
                type="number"
                min="0"
                max="200"
                value={formData.bloodPressureDiastolic}
                onChange={(e) => setFormData({ ...formData, bloodPressureDiastolic: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., 80"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Heart Rate (bpm)
              </label>
              <input
                type="number"
                min="0"
                max="300"
                value={formData.heartRate}
                onChange={(e) => setFormData({ ...formData, heartRate: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., 72"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Temperature (°C)
              </label>
              <input
                type="number"
                min="30"
                max="45"
                step="0.1"
                value={formData.temperature}
                onChange={(e) => setFormData({ ...formData, temperature: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., 36.5"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Respiratory Rate (breaths/min)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.respiratoryRate}
                onChange={(e) => setFormData({ ...formData, respiratoryRate: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., 16"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Oxygen Saturation (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={formData.oxygenSaturation}
                onChange={(e) => setFormData({ ...formData, oxygenSaturation: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., 98"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Weight (kg)
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={formData.weight}
                onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., 70.5"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Height (cm)
              </label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={formData.height}
                onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., 165.0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pain Level (0-10)
              </label>
              <input
                type="number"
                min="0"
                max="10"
                value={formData.painLevel}
                onChange={(e) => setFormData({ ...formData, painLevel: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="0 = no pain"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Blood Glucose (mg/dL)
              </label>
              <input
                type="number"
                min="0"
                value={formData.bloodGlucose}
                onChange={(e) => setFormData({ ...formData, bloodGlucose: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., 95"
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Additional observations or notes..."
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400"
            >
              {submitting ? 'Recording...' : 'Record Vital Signs'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface AddAllergyModalProps {
  residentId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const AddAllergyModal: React.FC<AddAllergyModalProps> = ({ residentId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    allergen: '',
    allergyType: 'food',
    severity: 'mild',
    reaction: '',
    discoveredDate: new Date().toISOString().split('T')[0],
    treatment: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await api.post(`/medical/residents/${residentId}/allergies`, {
        ...formData,
        treatment: formData.treatment || null,
        notes: formData.notes || null
      });

      if (response.data.success) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add allergy');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Add Allergy</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Allergen <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.allergen}
                onChange={(e) => setFormData({ ...formData, allergen: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., Peanuts, Penicillin, Pollen"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Allergy Type
              </label>
              <CustomSelect
                value={formData.allergyType}
                onChange={(v) => setFormData({ ...formData, allergyType: v })}
                options={[
                  { value: 'food', label: 'Food' },
                  { value: 'medication', label: 'Medication' },
                  { value: 'environmental', label: 'Environmental' },
                  { value: 'insect', label: 'Insect' },
                  { value: 'other', label: 'Other' },
                ]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Severity
              </label>
              <CustomSelect
                value={formData.severity}
                onChange={(v) => setFormData({ ...formData, severity: v })}
                options={[
                  { value: 'mild', label: 'Mild' },
                  { value: 'moderate', label: 'Moderate' },
                  { value: 'severe', label: 'Severe' },
                  { value: 'life-threatening', label: 'Life-Threatening' },
                ]}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reaction <span className="text-red-500">*</span>
              </label>
              <textarea
                required
                value={formData.reaction}
                onChange={(e) => setFormData({ ...formData, reaction: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Describe the allergic reaction (e.g., hives, difficulty breathing, swelling)"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discovered Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={formData.discoveredDate}
                onChange={(e) => setFormData({ ...formData, discoveredDate: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Treatment
              </label>
              <input
                type="text"
                value={formData.treatment}
                onChange={(e) => setFormData({ ...formData, treatment: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., Antihistamines, EpiPen, Avoid exposure"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Additional information or observations..."
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400"
            >
              {submitting ? 'Adding...' : 'Add Allergy'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface AddConditionModalProps {
  residentId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const AddConditionModal: React.FC<AddConditionModalProps> = ({ residentId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    conditionName: '',
    icd10Code: '',
    diagnosisDate: new Date().toISOString().split('T')[0],
    severity: 'moderate',
    ongoingTreatment: false,
    treatmentPlan: '',
    notes: '',
    nextReviewDate: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await api.post(`/medical/residents/${residentId}/conditions`, {
        ...formData,
        icd10Code: formData.icd10Code || null,
        treatmentPlan: formData.treatmentPlan || null,
        notes: formData.notes || null,
        nextReviewDate: formData.nextReviewDate || null
      });

      if (response.data.success) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add medical condition');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Add Medical Condition</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Condition Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.conditionName}
                onChange={(e) => setFormData({ ...formData, conditionName: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., Diabetes Type 2, Hypertension, Arthritis"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ICD-10 Code
              </label>
              <input
                type="text"
                value={formData.icd10Code}
                onChange={(e) => setFormData({ ...formData, icd10Code: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="e.g., E11.9, I10"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Diagnosis Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                required
                value={formData.diagnosisDate}
                onChange={(e) => setFormData({ ...formData, diagnosisDate: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Severity
              </label>
              <CustomSelect
                value={formData.severity}
                onChange={(v) => setFormData({ ...formData, severity: v })}
                options={[
                  { value: 'mild', label: 'Mild' },
                  { value: 'moderate', label: 'Moderate' },
                  { value: 'severe', label: 'Severe' },
                  { value: 'critical', label: 'Critical' },
                ]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Next Review Date
              </label>
              <input
                type="date"
                value={formData.nextReviewDate}
                onChange={(e) => setFormData({ ...formData, nextReviewDate: e.target.value })}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.ongoingTreatment}
                  onChange={(e) => setFormData({ ...formData, ongoingTreatment: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">Ongoing Treatment Required</span>
              </label>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Treatment Plan
              </label>
              <textarea
                value={formData.treatmentPlan}
                onChange={(e) => setFormData({ ...formData, treatmentPlan: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Describe the treatment plan for this condition..."
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Additional information or observations..."
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-base font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400"
            >
              {submitting ? 'Adding...' : 'Add Condition'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MedicalRecords;